import { Injectable, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  User,
  UserDefaults,
  Phone,
  DefaultPhoneNumber,
  Address,
  DefaultAddress,
  Email,
  DefaultMobilePhoneNumber,
} from '../model/user.model';
import { CountriesService } from '../services/countries.service';

@Injectable({
  providedIn: 'root',
})
export class UserFormService {
  constructor(private fb: FormBuilder, private countriesService: CountriesService) {}

  createUserForm(user: User = { ...UserDefaults }, isEditMode = false): FormGroup {
    const form = this.fb.group({
      enel_employee_id: [user.enel_employee_id, [Validators.required]],
      username: [user.username, [Validators.required, Validators.minLength(8), Validators.maxLength(70)]],
      userType: [user.userType, [Validators.required]],
      userClass: [user.userClass],
      title: [user.title, [Validators.maxLength(80)]],
      first_names: [user.first_names, [Validators.required]],
      last_names: [user.last_names, [Validators.required]],

      email: [user.email, [Validators.required, Validators.email]],
      phones: this.createPhonesFormArray(user.phones),
      addresses: this.createAddressesFormArray(user.addresses),

      brand: [user.brand],
      locale: [user.locale, [Validators.required]],
      timezone: [user.timezone, [Validators.required]],
      unit_system: [user.unit_system, [Validators.required]],
      home_application: [user.home_application],

      organization_name: [user.organization_name],
      organization_id: [user.organization_id],

      status: [user.status],
      flex_contact_id: [user.flex_contact_id],
      contact_id: [user.contact_id],
      ecrm_id: [user.ecrm_id, [this.ecrmValidator()]],

      twentyfour_seven_contact: [user.twentyfour_seven_contact],
      primary_contact: [user.primary_contact],
      sync_to_ecrm: [user.sync_to_ecrm],
      last_sync_date: [user.last_sync_date],
      send_activation: [true],
      api_key_id: [user.api_key_id || ''],
      key_display_label: [user.key_display_label || ''],
      asset_manager: [user.asset_manager ?? false],
    });
    return form;
  }

  disableFields(form: FormGroup, isEditMode = false) {
    const alwaysDisabled = ['flex_contact_id', 'ecrm_id', 'brand', 'organization_name', 'status', 'last_sync_date'];
    const disabledInEditMode = ['username', 'userType', 'userClass'];
    const disabledInCreateMode = ['home_application'];

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (alwaysDisabled.includes(key)) {
        control.disable();
      } else if (isEditMode && disabledInEditMode.includes(key)) {
        control.disable();
      } else if (!isEditMode && disabledInCreateMode.includes(key)) {
        control.disable();
      }
    });
  }

  disableSyncToEcrm(form: FormGroup, user: User) {
    if (user.sync_to_ecrm || this.isDeactivatedUser(user)) {
      form.get('sync_to_ecrm').disable();
    }
  }

  isDeactivatedUser(user: User) {
    return user.status.toUpperCase() === 'DEACTIVATED';
  }

  createPhonesFormArray(phones: Phone[] = []): FormArray {
    if (!phones || phones.length === 0) {
      return this.fb.array([
        this.createPhoneFormGroup(DefaultPhoneNumber),
        this.createPhoneFormGroup(DefaultMobilePhoneNumber),
      ]);
    }

    const mobilePhones = phones.filter((phone) => phone.phone_type === 'MOBILE');
    let mobilePhone = DefaultMobilePhoneNumber;
    if (mobilePhones.length > 0) {
      mobilePhone = this.getPrimaryPhone(mobilePhones);
    }

    const workPhones = phones.filter((phone) => phone.phone_type === 'WORK');
    let workPhone = DefaultPhoneNumber;
    if (workPhones.length > 0) {
      workPhone = this.getPrimaryPhone(workPhones);
    }

    return this.fb.array([this.createPhoneFormGroup(workPhone), this.createPhoneFormGroup(mobilePhone)]);
  }

  getPrimaryPhone(phones: Phone[]): Phone {
    if (phones.length == 1) {
      return phones[0];
    }
    return phones.find((phone) => phone.primary) || phones[0];
  }

  createPhoneFormGroup(phone: Phone): FormGroup {
    const phoneGroup = {
      primary: [phone.primary],
      phone_type: [phone.phone_type],
      extension: [phone.extension || ''],
      phone_number: [phone.phone_number, phone.phone_number ? Validators.required : null],
      country_code: [phone.country_code, phone.country_code ? Validators.required : null],
    };
    return this.fb.group(phoneGroup);
  }

  createAddressesFormArray(addresses: Address[] = []): FormArray {
    if (!addresses || addresses.length === 0) {
      return this.fb.array([this.createAddressFormGroup(DefaultAddress)]);
    }

    return this.fb.array(addresses.map((address) => this.createAddressFormGroup(address)));
  }

  createAddressFormGroup(address: Address): FormGroup {
    return this.fb.group({
      country: [address.country, [Validators.required]],
      country_name: [address.country_name],
    });
  }

  apiKeyFieldsValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const keyId = group.get('api_key_id')?.value?.trim() || '';
      const label = group.get('key_display_label')?.value?.trim() || '';

      const keyIdFilled = keyId.length > 0;
      const labelFilled = label.length > 0;

      if (keyIdFilled !== labelFilled) {
        return { apiKeyFieldsMismatch: true };
      }
      return null;
    };
  }

  ecrmValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const ecrmPattern = /^[A-Za-z0-9]{18}$/;
      if (!ecrmPattern.test(value)) {
        return { invalidEcrm: true };
      }

      return null;
    };
  }

  initialValidation(form: FormGroup, user: User) {
    if (user.sync_to_ecrm || this.isDeactivatedUser(user)) {
      form.get('sync_to_ecrm').disable();
    } else {
      form.get('sync_to_ecrm').enable();
    }

    if (user.phones.length !== 0) {
      form.get('phones').value.forEach((phone, index) => {
        if (phone.phone_number) {
          this.addPhoneValidation(form, index);
        }
      });
    }

    if (user.addresses.length !== 0) {
      this.addAddressValidation(form);
    }

    if (user.asset_manager) {
      form.get('asset_manager').disable();
    } else {
      form.get('asset_manager').enable();
    }
  }

  addPhoneValidation(form: FormGroup, index: number) {
    (form.get('phones') as FormArray).controls[index].get('phone_number').setValidators([Validators.required]);
    (form.get('phones') as FormArray).controls[index].get('phone_number').updateValueAndValidity();
    (form.get('phones') as FormArray).controls[index].get('country_code').setValidators([Validators.required]);
    (form.get('phones') as FormArray).controls[index].get('country_code').updateValueAndValidity();
  }

  addAddressValidation(form: FormGroup) {
    (form.get('addresses') as FormArray).controls[0].get('country').setValidators([Validators.required]);
    (form.get('addresses') as FormArray).controls[0].get('country').updateValueAndValidity();
  }

  prepareUserForSubmission(form: FormGroup): any {
    const addresses = this.validateAddresses(form.get('addresses').value, this.countriesService);
    const phones = this.validatePhones(form.get('phones').value);

    const formPayload = {
      external_user_ids: form.get('enel_employee_id') ? [{ ENELIT_ID: form.get('enel_employee_id').value }] : null,
      sync_to_ecrm: form.get('sync_to_ecrm')?.value || false,
      username: form.get('username').value?.trim(),
      first_names: form.get('first_names').value,
      last_names: form.get('last_names').value,
      email: form.get('email').value?.trim(),
      phones,
      addresses,
      title: form.get('title').value?.trim() ?? '',
      user_type: form.get('userType').value,
      locale: form.get('locale').value,
      timezone: form.get('timezone').value,
      brand: form.get('brand').value || undefined,
      unit_system: form.get('unit_system').value,
      home_application: form.get('home_application').value,
      organization: form.get('organization_id').value,
      ecrm_id: form.get('ecrm_id').value,
      contact_id: form.get('contact_id').value ?? undefined,
      primary_contact: form.get('primary_contact').value,
      twentyfour_seven_contact: form.get('twentyfour_seven_contact').value,
      user_class: form.get('userClass').value,
    };
    if (!form.get('enel_employee_id')) {
      delete formPayload.external_user_ids;
    }

    return formPayload;
  }

  validateAddresses(addresses: Address[], countriesService: CountriesService): Address[] | undefined {
    if (!addresses) {
      return undefined;
    }

    const allAddressesEmpty = addresses.every((address) => {
      if (!address) return true;
      const { country = '' } = address;
      return !country;
    });

    if (allAddressesEmpty) {
      return undefined;
    }

    const validAddresses = addresses
      .filter((address) => address && Object.values(address).some((val) => val !== null && val !== ''))
      .filter((address) => {
        const { country = '' } = address;
        return country?.trim().length > 0;
      });

    if (validAddresses.length === 0) {
      return undefined;
    }

    validAddresses.forEach((address) => {
      if (address.country) {
        address.country_name = address.country;
      }
    });
    return validAddresses;
  }

  validatePhones(phones: Phone[]): Phone[] | undefined {
    if (!phones || phones.every((phone) => !phone || !phone.phone_number || phone.phone_number.trim() === '')) {
      return undefined;
    }

    const validPhones = phones
      .filter((phone) => phone && phone.phone_number && phone.phone_number.trim() !== '')
      .filter((phone) => {
        const { phone_number = '', country_code } = phone;
        return (phone_number as unknown as string).length > 0 && country_code && parseInt(country_code, 10) > 0;
      });

    if (validPhones.length === 0) {
      return undefined;
    }

    return validPhones.map((phone) => {
      let { extension } = phone;

      if (extension === '') {
        extension = undefined;
      }
      return {
        primary: phone.primary,
        phone_type: phone.phone_type || 'WORK',
        phone_number: phone.phone_number.replace(/[^0-9]/gi, ''),
        extension,
        country_code: phone.country_code.replace(/[^0-9]/gi, ''),
      };
    });
  }

  addressFieldValidationCheck(formArray: FormArray, value: string) {
    const addressFormGroup = formArray.controls[0] as FormGroup;
    const countryCtrl = addressFormGroup.controls.country;

    if (value) {
      countryCtrl.clearValidators();
      countryCtrl.setErrors(null);
      addressFormGroup.setErrors(null);
      formArray.markAsTouched();
      formArray.setErrors(null);
      countryCtrl.setValidators([Validators.required]);
      countryCtrl.updateValueAndValidity();
    } else {
      countryCtrl.setValidators([Validators.required]);
      countryCtrl.updateValueAndValidity();
      if (countryCtrl.invalid) {
        addressFormGroup.setErrors({ invalid: true });
        formArray.setErrors({ invalid: true });
      }
    }
  }

  phoneFieldValidationCheck(
    form: FormGroup,
    hasAnyVoiceNotificationRole: boolean = false,
    hasAnySMSNotificationRole: boolean = false,
  ) {
    const phonesControl = form.get('phones');

    if (!phonesControl || !phonesControl.value) {
      return this.getEmptyValidationResult();
    }

    const regularPhoneResult = this.validateSinglePhone(form, 'WORK');
    const mobilePhoneResult = this.validateSinglePhone(form, 'MOBILE');

    const mobileIsRequired = hasAnySMSNotificationRole || mobilePhoneResult.hasAnyPhoneField;

    this.updatePhoneValidators(form, 1, mobileIsRequired);
    const regularIsRequired = regularPhoneResult.hasAnyPhoneField || (hasAnyVoiceNotificationRole && !mobileIsRequired);
    this.updatePhoneValidators(form, 0, regularIsRequired);

    return {
      hasAnyPhoneField: regularPhoneResult.hasAnyPhone,
      phoneFieldValidation: regularIsRequired,
      hasAnyMobilePhoneField: mobilePhoneResult.hasAnyPhone,
      mobilePhoneFieldValidation: mobileIsRequired,
    };
  }

  private validateSinglePhone(form: FormGroup, phoneType: string): { hasAnyPhone: boolean; hasAnyPhoneField: boolean } {
    const phonesControl = form.get('phones');

    //Find phone of given type, if multiple phones of the same type exist, find the primary one
    const phonesOfType = phonesControl.value.filter((phone) => phone.phone_type === phoneType);
    const singlePhone = phonesOfType.find((phone) => phone.primary) || phonesOfType[0];

    if (!phonesControl || !phonesControl.value || !singlePhone) {
      return { hasAnyPhone: false, hasAnyPhoneField: false };
    }

    const phone_number = singlePhone.phone_number ? singlePhone.phone_number : '';
    const country_code = singlePhone.country_code ? singlePhone.country_code : '';
    const extension = singlePhone.extension ? singlePhone.extension : '';

    const hasAnyPhoneField = phone_number.length > 0 || country_code.length > 0 || extension.length > 0;
    const hasAnyPhone = phone_number.length > 0;

    return { hasAnyPhone, hasAnyPhoneField };
  }

  private updatePhoneValidators(form: FormGroup, phoneIndex: number, isRequired: boolean): void {
    const phoneFormGroup = (form.get('phones') as FormArray).controls[phoneIndex] as FormGroup;
    const countryCodeCtrl = phoneFormGroup.get('country_code');
    const phoneNumberCtrl = phoneFormGroup.get('phone_number');

    if (isRequired) {
      countryCodeCtrl.setValidators([Validators.required]);
      countryCodeCtrl.markAsTouched();
      countryCodeCtrl.markAsDirty();
      countryCodeCtrl.updateValueAndValidity();

      phoneNumberCtrl.setValidators([Validators.required]);
      phoneNumberCtrl.markAsTouched();
      phoneNumberCtrl.markAsDirty();
      phoneNumberCtrl.updateValueAndValidity();
    } else {
      countryCodeCtrl.setValidators([]);
      countryCodeCtrl.setErrors(null);
      phoneNumberCtrl.setValidators([]);
      phoneNumberCtrl.setErrors(null);
      phoneFormGroup.setErrors(null);
    }
  }

  private getEmptyValidationResult() {
    return {
      hasAnyPhoneField: false,
      phoneFieldValidation: false,
      hasAnyMobilePhoneField: false,
      mobilePhoneFieldValidation: false,
    };
  }
}
